import { getIdb } from '../bridge/methods';
import moment from 'moment';
import getDoctorAllApts from './getDoctorAllApts';
import {
    APPOINTMENT_STATUS,
    completedQueueStatus,
    followupQueueStatus,
    otherQueueStatus,
    todayQueueStatus,
} from '../constants/appointment';
import { TReceipt } from '../pages/types/receipt';
import { track } from '../tracking';

export interface Summary {
    title: string;
    value: string;
}

export interface CountResponse {
    completed?: {
        count?: number;
    };
    upcoming?: {
        count?: number;
    };
    other?: {
        count?: number;
    };
    followup?: {
        count?: number;
    };
    today?: {
        count?: number;
        saCount?: number;
    };
}

export interface AppointmentsResponse {
    success: boolean;
    response?: { apt: Appointment[]; countResponse?: CountResponse };
}
export interface Appointment {
    date: string;
    archive: boolean;
    doctor_oid: string;
    aid: string;
    patient_uuid: string;
    patient_oid?: string | null;
    clinic_id: string;
    channel?: string;
    onsched_id: string;
    status: string;
    start_time: string;
    end_time: string;
    visit_start?: null;
    visit_end?: null;
    created_at: string;
    onApp?: boolean;

    sa_id?: null;
    tele_id?: null;

    prescription_id?: null;
    follow_up?: string;
    stage: string;
    patient: Patient;
    'visit-type': string;
    visit_type?: 'REG' | 'FLW';
    visit_checkin?: null | string;
    'type-display'?: string;
    rc?: boolean; // call back requested
    callback_requested?: string;
    cp?: boolean; // can park

    payment_id?: null;
    payment_status?: null | 'success' | 'failed' | 'created';
    can_pay?: boolean;
    amount?: null;
    visit_count?: number;

    can_abort?: boolean | null;

    billStatus?: 'unbilled' | 'paid' | 'pending';
    billTotal?: number;
    formData: {
        label: string;
        type: 'number' | 'string' | 'select';
        key: string;
        value: string | number;
    }[];
    last_visit_time?: string | null;

    non_emr?: boolean;
    doc_dwr_link?: string;
    comm_meta?: CommunicationMetaItem[];
    service?: Service;
    tags?: number[];
    receipts: Array<TReceipt>;
    token: number | null;
}

export enum FollowupMessagePurpose {
    FOLLOWUP_REMINDER = 'Follow-up',
    FOLLOWUP_ACCEPTANCE_RESPONSE = 'flw-yes',
    FOLLOWUP_REJECTION_RESPONSE = 'flw-no',
    FOLLOWUP_RESCHEDULE_RESPONSE = 'flw-res',
    FOLLOWUP_SLOT_CONFIRMATION_RESPONSE = 'flw-slot-conf',
    FOLLOWUP_FEELING_BETTER_RESPONSE = 'flw-feel-btr',
    FOLLOWUP_RESCHEDULE_CONFIRMATION = 'flw-res-cnf',

    APPOINTMENT_REMINDER = 'apt-rem',
    APPOINTMENT_REMINDER_YES_RESPONSE = 'apt-rem-yes',
    APPOINTMENT_REMINDER_NO_RESPONSE = 'apt-rem-no',
    APPOINTMENT_REMINDER_RESCHEDULE_RESPONSE = 'apt-rem-res',
    APPOINTMENT_REMINDER_NO_LATER_DATE_RESPONSE = 'apt-rem-no-later-date',
    APPOINTMENT_REMINDER_NO_FEELING_BETTER_RESPONSE = 'apt-rem-no-feeling-better',
}

export type CommunicationMetaItem = {
    purpose: FollowupMessagePurpose;
    type: 'Scheduled' | 'Immediate';
    status: 'Scheduled' | 'Sent' | 'Responded' | 'Cancelled' | 'Delivered' | 'Read';
    messageResponse?: 'Yes' | 'No' | string;
    messageId?: string; //Messenger Message Id for immediate stuff and some unique key combination with aid + index in comm_meta array
    sentAt?: string; //ISO Datetime
    schFor?: string;
};

export interface Patient {
    'is-p'?: boolean | null;
    ln?: string | null;
    mobile: string;
    at?: string | null;
    'dob-valid'?: boolean | null;
    dob?: string | null;
    fn?: string | null;
    mn?: string | null;
    uuid: string;
    oid?: string | null;
    email?: string | null;
    gen?: string | null;
    bloodgroup?: string | null;
    'is-d'?: boolean | null;
    fln: string;
    'health-ids'?: string[];
    amount_pending?: number;
}

export interface Service {
    c_at: string;
    mode?: string[] | null;
    price: number;
    archive: boolean;
    conf_id: string;
    eka_fee: number;
    pre_pay: boolean;
    conv_fee: number;
    currency: string;
    duration: number;
    nickname: string;
    owner_id: string;
    post_pay: boolean;
    service_id: number;
    service_name: string;
    book_ahead_days: number;
    appointment_type: 'FOLLOW_UP' | 'REGULAR';
    followup_validity?: null;
    allow_patient_booking: boolean;
}

export const getDateObjectFromYYYYMMDDWithTimezoneAdjusted = (dateString: string): Date => {
    const dateObject = new Date(dateString);
    const currentTimezoneOffset = dateObject.getTimezoneOffset();

    return new Date(dateObject.getTime() + currentTimezoneOffset * 60 * 1000);
};

const getStartAndEndDateIsoString = ({
    startDate,
    endDate,
}: {
    startDate?: string;
    endDate?: string;
}) => {
    const startDateObj = (startDate ? moment(startDate) : moment()).startOf('day');

    const endDateObj = (endDate ? moment(endDate) : moment(startDateObj)).endOf('day');

    return {
        startDateIsoString: startDateObj.toISOString(),
        endDateIsoString: endDateObj.toISOString(),
    };
};

export const fetchTodayQueue = async ({
    cid,
    startDate,
    endDate,
    docIds,
}: {
    cid: string;
    docIds: string[];
    startDate?: string;
    endDate?: string;
}): Promise<AppointmentsResponse> => {
    const appointments = await getTodaysAppointments({ queueType: 'today', clinicId: cid, docIds });
    return {
        success: true,
        response: {
            apt: appointments,
            countResponse: {
                today: {
                    count: appointments.length,
                },
            },
        },
    };
};

export const fetchCompletedAllTime = async ({
    cid,
    startDate,
    endDate,
    docIds,
}: {
    cid: string;
    startDate?: string;
    endDate?: string;
    docIds: string[];
}): Promise<AppointmentsResponse> => {
    const { startDateIsoString, endDateIsoString } = getStartAndEndDateIsoString({
        startDate,
        endDate,
    });

    if (isTodayAptBeingFetched({ startDateIsoString, endDateIsoString })) {
        const appointments = await getTodaysAppointments({
            queueType: 'completed',
            clinicId: cid,
            docIds,
        });
        return {
            success: true,
            response: {
                apt: appointments,
                countResponse: {
                    completed: {
                        count: appointments.length,
                    },
                },
            },
        };
    }

    const { apts } = await getDoctorAllApts({
        clinicId: cid,
        startTime: startDateIsoString,
        endTime: endDateIsoString,
        docIds,
        status: [APPOINTMENT_STATUS.COMPLETED, APPOINTMENT_STATUS.COMPLETED_NO_PRESCRIPTION],
        getArchived: false,
    });

    apts.sort(endTimeDescComparator);

    return {
        success: true,
        response: {
            apt: apts,
            countResponse: {
                completed: {
                    count: apts.length,
                },
            },
        },
    };
};

export const fetchOtherAppointmentAllTime = async ({
    cid,
    startDate,
    endDate,
    docIds,
}: {
    cid: string;
    startDate?: string;
    endDate?: string;
    docIds: string[];
}): Promise<AppointmentsResponse> => {
    const { startDateIsoString, endDateIsoString } = getStartAndEndDateIsoString({
        startDate,
        endDate,
    });

    if (isTodayAptBeingFetched({ startDateIsoString, endDateIsoString })) {
        const appointments = await getTodaysAppointments({
            queueType: 'others',
            clinicId: cid,
            docIds,
        });
        return {
            success: true,
            response: {
                apt: appointments,
                countResponse: {
                    other: {
                        count: appointments.length,
                    },
                },
            },
        };
    }

    const { apts } = await getDoctorAllApts({
        clinicId: cid,
        startTime: startDateIsoString,
        endTime: endDateIsoString,
        docIds,
        status: [
            APPOINTMENT_STATUS.CANCELLED,
            APPOINTMENT_STATUS.STAFF_CANCELLED,
            APPOINTMENT_STATUS.DOCTOR_CANCELLED,
            APPOINTMENT_STATUS.RESCHEDULED,
            APPOINTMENT_STATUS.STAFF_RESCHEDULED,
            APPOINTMENT_STATUS.DOCTOR_RESCHEDULED,
            APPOINTMENT_STATUS.ABORTED,
            APPOINTMENT_STATUS.NO_SHOW,
            APPOINTMENT_STATUS.NO_SHOW_DOCTOR,
            APPOINTMENT_STATUS.NO_SHOW_STAFF,
        ],
        getArchived: false,
    });

    apts.sort(startTimeBasedComparator);

    return {
        success: true,
        response: {
            apt: apts,
            countResponse: {
                other: {
                    count: apts.length,
                },
            },
        },
    };
};

export const fetchUpcomingAppointmentAllTime = async ({
    cid,
    startDate,
    endDate,
    docIds,
}: {
    cid: string;
    startDate?: string;
    endDate?: string;
    docIds: string[];
}): Promise<AppointmentsResponse> => {
    const { startDateIsoString, endDateIsoString } = getStartAndEndDateIsoString({
        startDate,
        endDate,
    });

    const { apts } = await getDoctorAllApts({
        clinicId: cid,
        startTime: startDateIsoString,
        endTime: endDateIsoString,
        docIds,
        status: [
            APPOINTMENT_STATUS.ONGOING,
            APPOINTMENT_STATUS.BOOKED,
            APPOINTMENT_STATUS.CHECKED_IN,
            APPOINTMENT_STATUS.PARKED,
        ],
        getArchived: false,
    });

    apts.sort(startTimeBasedComparator);

    return {
        success: true,
        response: {
            apt: apts,
            countResponse: {
                upcoming: {
                    count: apts.length,
                },
            },
        },
    };
};

export async function fetchFollowupQueue({
    cid,
    startDate,
    endDate,
    docIds,
}: {
    cid: string;
    startDate?: string;
    endDate?: string;
    docIds: string[];
}): Promise<AppointmentsResponse> {
    const { startDateIsoString, endDateIsoString } = getStartAndEndDateIsoString({
        startDate,
        endDate,
    });

    if (isTodayAptBeingFetched({ startDateIsoString, endDateIsoString })) {
        const appointments = await getTodaysAppointments({
            queueType: 'follow-up',
            clinicId: cid,
            docIds,
        });
        return {
            success: true,
            response: {
                apt: appointments,
                countResponse: {
                    followup: {
                        count: appointments.length,
                    },
                },
            },
        };
    }

    const { apts } = await getDoctorAllApts({
        clinicId: cid,
        startTime: startDateIsoString,
        endTime: endDateIsoString,
        docIds,
        status: [
            APPOINTMENT_STATUS.INITIATED,
            APPOINTMENT_STATUS.RESERVED,
            APPOINTMENT_STATUS.PROVISIONAL_CANCELLED,
            APPOINTMENT_STATUS.RESCHEDULED,
        ],
        getArchived: false,
        visitType: 'FLW',
    });

    apts.sort(startTimeBasedComparator);

    return {
        success: true,
        response: {
            apt: apts,
            countResponse: {
                followup: {
                    count: apts.length,
                },
            },
        },
    };
}

async function getTodaysAppointments({
    queueType,
    clinicId,
    docIds,
}: {
    queueType: 'today' | 'completed' | 'follow-up' | 'others';
    clinicId: string;
    docIds: string[];
}) {
    const idb = getIdb();
    const startTime = moment().startOf('day');
    const endTime = moment().endOf('day');

    const startDateIsoString = startTime.toISOString();
    const endDateIsoString = endTime.toISOString();
    const today = moment().format('YYYY-MM-DD');

    const aptConfig = await idb?.table('misc')?.get('aptConfig');

    if (!!aptConfig?.config && aptConfig.config.localAptDate !== today) {
        //today has changed.
        //flush appointment db
        //flush the idb misc key
        idb.todayAppointments.clear();
        idb.misc.delete('aptConfig');
        aptConfig.config = {
            ...aptConfig.config,
            localAptUpdatedAt: undefined,
            localAptDate: undefined,
        };
    }

    const lastUpdatedAt = aptConfig?.config?.localAptUpdatedAt?.[clinicId];

    const locallyKeptApts: Appointment[] =
        (await idb?.todayAppointments?.where({ clinic_id: clinicId })?.toArray()) || [];

    const { apts, time_cursor } = await getDoctorAllApts({
        clinicId,
        startTime: startDateIsoString,
        endTime: endDateIsoString,
        since: lastUpdatedAt ? lastUpdatedAt : undefined,
        docIds,
    });

    idb?.todayAppointments
        ?.bulkPut?.(apts.map((item) => ({ ...item })))
        .then(() => {
            idb?.misc?.put?.({
                ...aptConfig,
                key: 'aptConfig',
                config: {
                    ...aptConfig?.config,
                    localAptDate: today,
                    localAptUpdatedAt: {
                        ...aptConfig?.config?.localAptUpdatedAt,
                        [clinicId]: time_cursor,
                    },
                },
            });
        })
        .catch((e: any) => {
            track('QUEUE_IDB_FAILURE', { error: e?.message || e, queue: 'v1' });
        });

    const updatedAidSet = new Set<string>();
    apts.forEach((item) => updatedAidSet.add(item.aid));

    const consolidatedAllApts = [
        ...(locallyKeptApts || []).filter((item) => !updatedAidSet.has(item.aid)),
        ...apts,
    ];

    const appointmentsToReturn: Appointment[] = [];

    for (let i = 0; i < consolidatedAllApts.length; i++) {
        const apt = consolidatedAllApts[i];
        if (apt.archive) {
            continue;
        }

        const status = apt.status as APPOINTMENT_STATUS;

        if (queueType === 'today' && todayQueueStatus.includes(status)) {
            appointmentsToReturn.push(apt);
        } else if (queueType === 'completed' && completedQueueStatus.includes(status)) {
            appointmentsToReturn.push(apt);
        } else if (queueType === 'others' && otherQueueStatus.includes(status)) {
            appointmentsToReturn.push(apt);
        } else if (
            queueType === 'follow-up' &&
            apt.visit_type == 'FLW' &&
            followupQueueStatus.includes(status)
        ) {
            appointmentsToReturn.push(apt);
        }
    }

    if (queueType === 'completed') {
        appointmentsToReturn.sort(endTimeDescComparator);
    } else {
        appointmentsToReturn.sort(startTimeBasedComparator);
    }

    return appointmentsToReturn;
}

function isTodayAptBeingFetched({
    startDateIsoString,
    endDateIsoString,
}: {
    startDateIsoString: string;
    endDateIsoString: string;
}) {
    const formattedStartDate = moment(startDateIsoString);
    const formattedEndDate = moment(endDateIsoString);
    const formattedTodayDate = moment();

    const isTodayApt =
        formattedTodayDate.isSame(formattedStartDate, 'day') &&
        formattedStartDate.isSame(formattedEndDate, 'day');

    return isTodayApt;
}

function startTimeBasedComparator(a: Appointment, b: Appointment) {
    return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
}

function endTimeDescComparator(a: Appointment, b: Appointment) {
    return new Date(b.visit_end!).getTime() - new Date(a.visit_end!).getTime();
}
